import {useState, useCallback, useEffect, useRef} from 'react';
import api from "../../utils/api";

interface UseTransactionVerificationProps {
    onSuccess: (message: string) => void;
    onError: (title: string, message?: string) => void;
}

export const usePaySubscribe = ({
                                    onSuccess,
                                    onError
                                }: UseTransactionVerificationProps) => {
    const [verifying, setVerifying] = useState<boolean>(false);
    const [transactionIdToVerify, setTransactionIdToVerify] = useState<string | null>(null);

    const latestProps = useRef({onSuccess, onError});
    useEffect(() => {
        latestProps.current = {onSuccess, onError};
    });

    const verifyTransaction = useCallback((id: string | null) => {
        if (!id) {
            latestProps.current.onError('Error', 'No transaction ID to verify.');
            setVerifying(false);
            return;
        }

        setVerifying(true);

        api.get(`/subscribe/verify/${id}`).then(res => {
            if (res.data.success) {
                latestProps.current.onSuccess('Payment has been verified');
            } else {
                latestProps.current.onError('Payment has not been verified');
            }
        }).finally(() => {
            setTransactionIdToVerify(null);
            setVerifying(false)
        });
    }, []);

    useEffect(() => {
        const handler = () => {
            if (transactionIdToVerify) {
                verifyTransaction(transactionIdToVerify);
            }
        };

        if (transactionIdToVerify) {
            window.addEventListener('focus', handler);

            return () => {
                window.removeEventListener('focus', handler);
            };
        }
    }, [transactionIdToVerify, verifyTransaction]);

    const openPayment = useCallback((code: string, url: string | undefined) => {
        setTransactionIdToVerify(code);
        window.open(url, 'forge-payment');
    }, []);

    const initializeTransaction = useCallback((url: string) => {
        api.get(url).then(res => {
            if (res.data.code && res.data.link) {
                openPayment(res.data.code, res.data.link)
            } else {
                latestProps.current.onError('Error', 'Failed to get payment link or transaction ID.');
            }
        }).catch(err => {
            if (err.response?.data?.message) {
                latestProps.current.onError('Error', err.response.data.message);
            } else {
                latestProps.current.onError('Error', 'An unknown error occurred while initiating transaction.');
            }
        });
    }, [openPayment]);

    return {
        verifying,
        verifyTransaction,
        openPayment,
        initializeTransaction
    };
};
